import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Box,
  Heading,
  Text,
  Spinner,
  VStack,
  Divider,
  useToast,
} from "@chakra-ui/react";
import Seo from "../../seo/Seo";
import { articleSchema, webPageSchema } from "../../seo/schema";
import { absoluteUrl } from "../../seo/siteConfig";

const BlogDetails = () => {
  const { id: paramId } = useParams();
  const location = useLocation(); // Access the location object
  const [blog, setBlog] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();
  const id = location.state?.id || paramId;
  console.log(id);
  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await fetch(
          `https://api.qualiteeth.co.in/api/getBlog/${id}`
        ); // Replace with your API endpoint
        // const response = await fetch(`http://localhost:5000/api/getBlog/${id}`);
        if (!response.ok) {
          throw new Error("Failed to fetch blog details.");
        }
        const data = await response.json();
        setBlog(data.Blog);
      } catch (err) {
        setError(err.message || "Something went wrong.");
        toast({
          title: "Error",
          description: err.message || "Failed to fetch blog details.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlog();
  }, [id, toast]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="100vh"
      >
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    // An article we can't load must not be indexed under its own URL.
    return (
      <Box textAlign="center" py={10} px={6}>
        <Seo
          title="Article Unavailable | Qualiteeth Dental Care"
          description="This article could not be loaded. Browse the Qualiteeth Dental Care blog for more dental health guidance."
          noindex
        />
        <Heading as="h1" size="xl" mb={4}>
          Article unavailable
        </Heading>
        <Text>We couldn't load this article just now. Please try again shortly.</Text>
      </Box>
    );
  }

  return (
    <Box bg="#fceee5">
      <Seo
        title={`${blog.title} | Qualiteeth Dental Care, Jaipur`}
        description={blog.description}
        type="article"
        image={blog.cover_image || undefined}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: blog.title, path: location.pathname },
        ]}
        schemas={[
          webPageSchema({
            url: absoluteUrl(location.pathname),
            title: blog.title,
            description: blog.description,
            image: blog.cover_image,
            datePublished: blog.created_at,
            dateModified: blog.updated_at || blog.created_at,
          }),
          articleSchema({
            url: absoluteUrl(location.pathname),
            title: blog.title,
            description: blog.description,
            image: blog.cover_image,
            datePublished: blog.created_at,
            dateModified: blog.updated_at || blog.created_at,
            author: blog.author,
          }),
        ]}
      />
      <Box maxW="4xl" mx="auto" py={8} px={4} marginTop="80px">
        <Heading
          as="h1"
          fontSize={{ base: "2xl", md: "5xl" }}
          mb={6}
          textAlign="center"
          fontWeight="light"
        >
          {blog.title}
        </Heading>
        <Text fontSize="sm" color="gray.600" mb={4} fontStyle="italic">
          {`By ${blog.author} - ${new Date(blog.created_at).toLocaleDateString(
            "en-US",
            { month: "long", day: "numeric", year: "numeric" }
          )}`}
        </Text>
        <Divider mb={6} />
        <VStack spacing={4} align="stretch">
          {blog.content.map((section, index) => (
            <Box key={index}>
              {section.subheading && (
                <Heading
                  as="h2"
                  fontSize={{ base: "lg", md: "xl" }}
                  fontWeight="bold"
                  mt={4}
                >
                  {section.subheading}
                </Heading>
              )}
              <Text mt={2} fontWeight="light">
                {section.text}
              </Text>
            </Box>
          ))}
        </VStack>
      </Box>
    </Box>
  );
};

export default BlogDetails;
